// "+ Tape": put a song on one of your tapes, or start a new tape with it — from a song's preview
// window, its page, or live while it plays in the River.
import { useEffect, useState } from "react";
import { create } from "zustand";
import { api, ApiError } from "../api";
import { currentRoute, Link } from "../lib/router";
import { useSession } from "../session";
import { Modal } from "../ui/Modal";

interface TapeSummary {
  id: string;
  name: string;
  songs: number;
}

/** The one "+ Tape" dialog of the page (App.tsx renders it), so it doesn't live inside a moving
 *  River card — the card may scroll away while you choose. */
export const useTapeDialog = create<{ loopId: string | null; open: (id: string | null) => void }>((set) => ({
  loopId: null,
  open: (loopId) => set({ loopId }),
}));

export function TapeDialogHost() {
  const loopId = useTapeDialog((s) => s.loopId);
  return loopId ? <AddToTapeDialog loopId={loopId} onClose={() => useTapeDialog.getState().open(null)} /> : null;
}

export function AddToTapeButton({
  loopId,
  className = "btn btn-ghost",
  label = "+ Tape",
  style,
}: {
  loopId: string;
  className?: string;
  label?: string;
  style?: React.CSSProperties;
}) {
  return (
    <button
      type="button"
      className={className}
      style={style}
      onClick={(e) => {
        e.stopPropagation();
        useTapeDialog.getState().open(loopId);
      }}
      aria-label="Add this song to one of your tapes"
      title="Add to a tape"
      data-testid="add-to-tape"
    >
      {label}
    </button>
  );
}

export function AddToTapeDialog({ loopId, onClose }: { loopId: string; onClose: () => void }) {
  const user = useSession((s) => s.user);
  const [tapes, setTapes] = useState<TapeSummary[] | null>(null);
  const [name, setName] = useState("");
  const [msg, setMsg] = useState<{ text: string; tapeId?: string; error?: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.nickname) return;
    api<{ tapes: TapeSummary[] }>("GET", "/api/tapes")
      .then((r) => setTapes(r.tapes))
      .catch(() => setTapes([]));
  }, [user?.nickname]);

  const run = async (f: () => Promise<{ tape: { id: string; name: string } }>) => {
    setBusy(true);
    try {
      const { tape } = await f();
      setMsg({ text: `Added to “${tape.name}”.`, tapeId: tape.id });
      setTapes((ts) => (ts ?? []).map((t) => (t.id === tape.id ? { ...t, songs: t.songs + 1 } : t)));
    } catch (e) {
      setMsg({ text: e instanceof ApiError ? e.message : "Couldn't add the song.", error: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Add to a tape" onClose={onClose}>
      {!user?.nickname ? (
        <p>
          <Link href={`/signin?next=${encodeURIComponent(currentRoute())}`}>Sign in</Link> to make your own tapes.
        </p>
      ) : (
        <div className="stack add-to-tape" data-testid="tape-dialog">
          {tapes === null ? (
            <p className="muted">Loading your tapes…</p>
          ) : tapes.length > 0 ? (
            <ul className="tape-choices">
              {tapes.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    className="btn"
                    disabled={busy || t.songs >= 100}
                    onClick={() =>
                      void run(() => api("POST", `/api/tapes/${encodeURIComponent(t.id)}/songs`, { loopId }))
                    }
                    data-testid={`tape-choice-${t.name}`}
                  >
                    {t.name} <span className="muted small">· {t.songs}/100</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">You have no tapes yet — start one with this song.</p>
          )}
          <form
            className="row"
            onSubmit={(e) => {
              e.preventDefault();
              const n = name.trim();
              if (!n) return;
              void run(async () => {
                const r = await api<{ tape: { id: string; name: string } }>("POST", "/api/tapes", { name: n, loopId });
                setTapes((ts) => [{ id: r.tape.id, name: r.tape.name, songs: 0 }, ...(ts ?? [])]);
                setName("");
                return r;
              });
            }}
          >
            <label className="field">
              <span>New tape</span>
              <input
                value={name}
                maxLength={40}
                placeholder="Name"
                onChange={(e) => setName(e.target.value)}
                data-testid="tape-new-name"
              />
            </label>
            <button type="submit" className="btn btn-primary" disabled={busy || !name.trim()} data-testid="tape-new">
              Create &amp; add
            </button>
          </form>
          {msg && (
            <p role={msg.error ? "alert" : "status"} className={msg.error ? "error" : ""} data-testid="tape-msg">
              {msg.text} {msg.tapeId && <Link href={`/tapes/${encodeURIComponent(msg.tapeId)}`}>Open the tape</Link>}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
